import {
  useBalance,
  useContractLoader,
  useContractReader,
  useGasPrice,
  useOnBlock,
  useUserProviderAndSigner,
} from 'eth-hooks';
import { useExchangeEthPrice } from 'eth-hooks/dapps/dex';
import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { Route, Switch } from 'react-router-dom';

import 'antd/dist/antd.css';
import 'react-tailwind-table/dist/index.css';
import './App.css';

import { LayoutDefault } from './layouts';
import { Funnel, NotFound } from './pages';
import { PrivateRoute, Account, NetworkDisplay } from './components';

import { NETWORKS } from './constants/scaffold-eth';

import { Transactor, Web3ModalSetup } from './helpers';
import { useStaticJsonRPC, useUserProvider } from './hooks';

// contracts
import externalContracts from './contracts/external_contracts';
import deployedContracts from './contracts/hardhat_contracts.json';

const { ethers } = require('ethers');

// 📡 What chain are your contracts deployed to?
const initialNetwork = NETWORKS.localhost; // <------- select your target frontend network (localhost, rinkeby, xdai, mainnet)

const DEBUG = false;
const NETWORKCHECK = true;
const USE_BURNER_WALLET = false; // toggle burner wallet feature

const web3Modal = Web3ModalSetup();

// 🛰 providers
const providers = [
  //'https://eth-mainnet.gateway.pokt.network/v1/lb/xxxx',
  //`https://eth-mainnet.alchemyapi.io/v2/${ALCHEMY_KEY}`,
];

function App(props) {
  // specify all the chains your app is available on. Eg: ['localhost', 'mainnet', ...otherNetworks ]
  // reference './constants/scaffold-eth.js' for other networks
  const networkOptions = [initialNetwork.name, 'goerli', 'mainnet'];

  const [injectedProvider, setInjectedProvider] = useState();
  const [address, setAddress] = useState();
  const [selectedNetwork, setSelectedNetwork] = useState(networkOptions[0]);

  const targetNetwork = NETWORKS[selectedNetwork];

  // 🔭 block explorer URL
  const blockExplorer = targetNetwork.blockExplorer;

  // load all your providers
  const localProvider = useStaticJsonRPC([
    process.env.REACT_APP_PROVIDER ? process.env.REACT_APP_PROVIDER : targetNetwork.rpcUrl,
  ]);
  const mainnetProvider = useStaticJsonRPC(providers);

  if (DEBUG) console.log(`Using ${selectedNetwork} network`);

  // 🛰 providers
  if (DEBUG) console.log('📡 Connecting to Mainnet Ethereum');

  const logoutOfWeb3Modal = async () => {
    web3Modal.clearCachedProvider();
    if (injectedProvider && injectedProvider.provider && typeof injectedProvider.provider.disconnect == 'function') {
      await injectedProvider.provider.disconnect();
    }
    setTimeout(() => {
      window.location.reload();
    }, 1);
  };

  // 💵 This hook will get the price of ETH from 🦄 Uniswap:
  const price = useExchangeEthPrice(targetNetwork, mainnetProvider);

  // 🔥 This hook will get the price of Gas from ⛽️ EtherGasStation
  const gasPrice = useGasPrice(targetNetwork, 'fast');
  // Use your injected provider from 🦊 Metamask or if you don't have it then instantly generate a 🔥 burner wallet.
  const userProviderAndSigner = useUserProviderAndSigner(injectedProvider, localProvider, USE_BURNER_WALLET);
  const userSigner = userProviderAndSigner.signer;

  useEffect(() => {
    async function getAddress() {
      if (userSigner) {
        const newAddress = await userSigner.getAddress();
        setAddress(newAddress);
      }
    }
    getAddress();
  }, [userSigner]);

  // You can warn the user if you would like them to be on a specific network
  const localChainId = localProvider && localProvider._network && localProvider._network.chainId;
  const selectedChainId =
    userSigner && userSigner.provider && userSigner.provider._network && userSigner.provider._network.chainId;

  // For more hooks, check out 🔗eth-hooks at: https://www.npmjs.com/package/eth-hooks

  // The transactor wraps transactions and provides notificiations
  const tx = Transactor(userSigner, gasPrice);

  const yourLocalBalance = useBalance(localProvider, address);

  // Just plug in different 🛰 providers to get your balance on different chains:
  const yourMainnetBalance = useBalance(mainnetProvider, address);

  // const contractConfig = useContractConfig();

  const contractConfig = { deployedContracts: deployedContracts || {}, externalContracts: externalContracts || {} };

  // Load in your local 📝 contract and read a value from it:
  const readContracts = useContractLoader(localProvider, contractConfig);

  // If you want to make 🔐 write transactions to your contracts, use the userSigner:
  const writeContracts = useContractLoader(userSigner, contractConfig, localChainId);

  // EXTERNAL CONTRACT EXAMPLE:
  //
  // If you want to bring in the mainnet DAI contract it would look like:
  const mainnetContracts = useContractLoader(mainnetProvider, contractConfig);

  // If you want to call a function on a new block
  useOnBlock(mainnetProvider, () => {
    console.log(`⛓ A new mainnet block is here: ${mainnetProvider._lastBlockNumber}`);
  });

  /*
  // Then read your DAI balance like:
  const myMainnetDAIBalance = useContractReader(mainnetContracts, 'DAI', 'balanceOf', [
    '0x34aA3F359A9D614239015126635CE7732c18fDF3',
  ]);

  // keep track of a variable from the contract in the local React state:
  //const purpose = useContractReader(readContracts, 'YourContract', 'purpose');

  const addressFromENS = useResolveName(mainnetProvider, "austingriffith.eth");
  console.log("🏷 Resolved austingriffith.eth as:",addressFromENS)
  */

  const userProvider = useUserProvider(injectedProvider);

  const loadWeb3Modal = useCallback(async () => {
    const provider = await web3Modal.connect();
    setInjectedProvider(new ethers.providers.Web3Provider(provider));

    provider.on('chainChanged', chainId => {
      console.log(`chain changed to ${chainId}! updating providers`);
      setInjectedProvider(new ethers.providers.Web3Provider(provider));
    });

    provider.on('accountsChanged', () => {
      console.log(`account changed!`);
      setInjectedProvider(new ethers.providers.Web3Provider(provider));
    });

    // subscribe to session disconnection
    provider.on('disconnect', (code, reason) => {
      console.log(code, reason);
      logoutOfWeb3Modal();
    });
    // eslint-disable-next-line
  }, [setInjectedProvider]);

  /*
  // open metamask on page load if already connected
  useEffect(() => {
    if (web3Modal.cachedProvider) {
      loadWeb3Modal();
    }
  }, [loadWeb3Modal]);
  */

  // TODO: move to context
  const walletIsConnected = injectedProvider && injectedProvider.getSigner && injectedProvider.getSigner()._isSigner;
  const [walletOwnershipIsVerified, setWalletOwnershipIsVerified] = useState(false);

  const faucetAvailable = localProvider && localProvider.connection && targetNetwork.name.indexOf('local') !== -1;

  // TODO: move to context
  const accountView = (
    <Account
      address={address}
      connectText={
        <>
          Wallet verbinden<span className="ml-2">🔗</span>
        </>
      }
      //ensProvider={ensProvider}
      loadWeb3Modal={loadWeb3Modal}
      logoutOfWeb3Modal={logoutOfWeb3Modal}
      userSigner={userSigner}
      localProvider={localProvider}
      mainnetProvider={mainnetProvider}
      price={price}
      walletIsConnected={walletIsConnected}
      web3Modal={web3Modal}
      blockExplorer={blockExplorer}
    />
  );

  return (
    <div id="App" className="h-full">
      <Switch>
        <Route path={['/', '/login']}>
          <LayoutDefault>
            <Switch>
              <Route exact path="/">
                <Funnel
                  tx={tx}
                  externalContracts={externalContracts}
                  writeContracts={writeContracts}
                  gasPrice={gasPrice}
                  address={address}
                  userProvider={userProvider}
                  localProvider={localProvider}
                  walletIsConnected={walletIsConnected}
                  walletOwnershipIsVerified={walletOwnershipIsVerified}
                  setWalletOwnershipIsVerified={setWalletOwnershipIsVerified}
                  accountView={accountView}
                />
              </Route>
            </Switch>

            {/*
            <div
              style={{
                position: 'fixed',
                bottom: '5px',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                flexWrap: 'wrap',
                gap: '15px',
                alignItems: 'center',
                justifyContent: 'center',
                pointerEvents: 'none',
              }}
            >

              <PoweredByMemberrTag />
              <SafeConnectionTag />
            </div>
            */}
          </LayoutDefault>
        </Route>

        <PrivateRoute path={[]}>
          <LayoutDefault>
            {/* 🗺 extra UI like gas price, eth price, faucet, and support: */}
            <div className="fixed bottom-6 right-6">
              <div className="mb-1 space-x-2">
                <a
                  href="https://t.me/joinchat/TODO"
                  className="inline-flex items-center px-3 py-0.5 rounded-full text-base font-normal bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-white"
                >
                  💬 support
                </a>
              </div>

              {/* if the local provider has a signer, let's show the faucet:*/}
              {/*faucetAvailable && <Faucet localProvider={localProvider} price={price} ensProvider={mainnetProvider} />*/}
            </div>
            {/* global notification live region, render this permanently at the end of the document */}
            <div aria-live="assertive" className="fixed inset-0 flex items-start px-4 pt-20 pb-6 pointer-events-none">
              <div className="w-full flex flex-col items-end space-y-4">
                {/* alert if wrong network is selected */}
                <NetworkDisplay
                  NETWORKCHECK={NETWORKCHECK}
                  localChainId={localChainId}
                  selectedChainId={selectedChainId}
                  targetNetwork={targetNetwork}
                />
                {/*yourLocalBalance.lte(ethers.BigNumber.from('0')) && (
                 <FaucetHint localProvider={localProvider} targetNetwork={targetNetwork} address={address} />
                )*/}
              </div>
            </div>
          </LayoutDefault>
        </PrivateRoute>
      </Switch>
    </div>
  );
}

export default App;
